import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useMicVAD } from "@ricky0123/vad-react";
import {
  Message,
  ConversationTypes,
  WebRTCError,
  ValidLanguages,
} from "../../utils/types";
import { useNavigate, useLocation } from "react-router-dom";

import WarningPopup from "../warningPopup/WarningPopup";
import bubble from "./../../../../assets/images/bubble.svg";
import secureIcon from "./../../../../assets/images/secure.svg";
import voice from "./../../../../assets/images/voice.svg";
import DDxInputSelectorBg from "./../../../../assets/images/ddx-input-selector-bg.webp";
import end from "./../../../../assets/images/end.svg";
import chat from "./../../../../assets/images/aa-close.svg";
import muteMic from "./../../../../assets/images/mute-mic.svg";
import activeMic from "./../../../../assets/images/aa-mic.svg";
import close from "./../../../../assets/images/close-outline.svg";
import patientGreeting from "./../../../../assets/audio/patient-greetings-english.mp3";
import {
  INPUT_AUDIO_NOISE_REDUCTION_TYPE,
  DEFAULT_AGENT_NAME,
  DEFAULT_GENDER,
  DEFAULT_VOICE,
  JWT_TOKEN,
  LangSpecificMsg,
  languageOptions,
  OPENAI_SUPPORTED_LANGUAGES,
} from "../../utils/constants";
import {
  EXTRACT_OPTIONS_COUNT,
  SYMPTOM_COLLECTOR_TRANSCRIPTION_MODEL,
  WEBSOCKET_URL,
} from "../../../../envConstants";
import {
  fetchExtractOptions,
  getInstructions,
  getSummary,
  saveConversationData,
  startAzureOpenAISession,
} from "../../service";
import { Button } from "antd";
import Lottie from "lottie-react";
import * as voiceWave from "./../../../../assets/animations/voice-wave.json";
import * as aiBuble from "./../../../../assets/animations/ai-buble.json";
import {
  checkAndRequestPermissions,
  generateUUID,
  getDecodedSCToken,
  initializeAudioContext,
  isConnectionActive,
  isIOS,
  processConversationSummary,
} from "../../symptomCollectorHelper";
import { establishWebRtcConnection } from "../../../appointmentAgent/service";
import { trackEvent } from "../../../../utils/helper";
import dayjs from "dayjs";
import ConfirmationPopup from "../../../appointmentAgent/components/confirmationPopup/ConfirmationPopup";
import AnimatedMessage from "../../../appointmentAgent/components/bookAppointment/AnimatedMessage";
import VoiceBubble from "../../../appointmentAgent/components/bookAppointment/VoiceBubble";
import VoiceWaveVisualizer from "../../../appointmentAgent/components/bookAppointment/WaveVisualizer";
import speakLoading from "./../../../../assets/animations/speak-loading.gif";
import chatTypingIdicator from "./../../../../assets/animations/typing.gif";
import { ConfirmSubmitPopup } from "../confirmSubmitPopup/ConfirmSubmitPopup";
import Layout from "../../../../commonComponents/Layout";

const Conversation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const isSafari = useMemo(
    () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
    []
  );
  const isIOSDevice = useMemo(
    () => /iPad|iPhone|iPod/.test(navigator.userAgent),
    []
  );
  const jwtToken = new URLSearchParams(location.search).get(JWT_TOKEN) || "";
  const decodedToken: any = getDecodedSCToken(jwtToken);
  const {
    um_id,
    patient_unique_id,
    patient_name,
    patient_contact_no,
    doctor_name,
    hm_id,
    pam_id,
    agent_name,
    agent_gender,
    agent_voice,
    dp_name,
    dp_id,
  } = decodedToken?.user || {};

  const selectedLanguage = state?.selectedLang || "english";
  const avatarImage = state?.avatarImage || "";
  const type = state?.type || "chat";
  const conversationId = state?.id || "";

  const LangSpecificMsgData =
    LangSpecificMsg[(selectedLanguage as ValidLanguages) || "english"];

  // State with types
  const [messages, setMessages] = useState<Message[]>([]);
  const initialMessageCountRef = useRef<number>(0);
  const [value, setValue] = useState<string>("");
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isVoiceSelected, setIsVoiceSelected] = useState<boolean>(
    type === "voice"
  );
  const [showWarningPopup, setShowWarningPopup] = useState<boolean>(false);
  const [showExitPopup, setShowExitPopup] = useState<boolean>(false);
  const [options, setOptions] = useState<any>([]);
  const [isEnd, setIsEnd] = useState<boolean>(false);
  const [transcribedParagraph, setTranscribedParagraph] = useState("");
  const [peerConnection, setPeerConnection] =
    useState<RTCPeerConnection | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [soundLevel, setSoundLevel] = useState(1);
  const [conversationChunks, setConversationChunks] = useState<Blob[]>([]);
  const [mergedRecorder, setMergedRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isAudioCompleted, setIsAudioCompleted] = useState(false);
  const [instructions, setInstructions] = useState("");

  // Refs with types
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const lastTranscriptRef = useRef<string>("");
  const userTranscriptionRef = useRef<ConversationTypes[]>([]);
  const agentTranscriptionRef = useRef<ConversationTypes[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameRef = useRef<number>();
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);

  const audioBlobsRef = useRef<Blob[]>([]); // New ref for storing audio blobs

  const messageContainerRef = useRef<HTMLDivElement>(null);

  const streamRef = useRef<MediaStream | null>(null);

  // Replace state with ref
  const currentIncrementalTextRef = useRef<string>("");

  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const vadActiveRef = useRef<boolean>(false);
  const [isRecording, setIsRecording] = useState(false);

  const fetchInstructions = async () => {
    const instructionsRes = await getInstructions(
      selectedLanguage,
      state?.conversation,
      agent_name ?? DEFAULT_AGENT_NAME,
      agent_gender ?? DEFAULT_GENDER
    );
    setInstructions(instructionsRes);
  };

  useEffect(() => {
    if (selectedLanguage && !instructions && isVoiceSelected) {
      fetchInstructions();
    }
  }, [selectedLanguage, isVoiceSelected]);

  const showSkeletonLoaderData = useMemo(() => {
    return (
      !isVoiceSelected &&
      !isEnd &&
      options?.length === 0 &&
      (messages?.length === 0 ||
        messages?.[messages.length - 1]?.type === "user")
    );
  }, [isVoiceSelected, messages, isEnd, options]);

  // Find the last agent message index to only animate the latest one
  const lastAgentMessageIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.type === "agent") {
        return i;
      }
    }
    return -1;
  }, [messages]);

  // For voice mode: only show the last message (similar to BookAppointment)
  const renderVoiceMessages = useMemo(() => {
    if (messages.length === 0) return null;
    
    const lastMessage = messages[messages.length - 1];
    const isNewMessage = messages.length - 1 >= initialMessageCountRef.current;
    
    if (
      (selectedLanguage === "gujarati" &&
        lastMessage?.type !== "user") ||
      selectedLanguage !== "gujarati"
    ) {
      return (
        <div
          className="h-[600px] overflow-y-auto text-2xl"
          ref={messageContainerRef}
          style={{ opacity: "0.6", lineHeight: "42px" }}
        >
          {isNewMessage ? (
            <AnimatedMessage
              key={`voice-${lastMessage?.timestamp || messages.length - 1}`}
              text={lastMessage?.text || ""}
              speed={150}
              isLoading={isLoading}
              className="text-sm leading-5 text-gray-800 font-normal"
            />
          ) : (
            <p className="text-sm leading-5 text-gray-800 font-normal">
              {lastMessage?.text || ""}
            </p>
          )}
        </div>
      );
    }
    
    return null;
  }, [messages, isLoading, selectedLanguage]);

  const renderMessages = useMemo(() => {
    return (
      <>
        {messages.map((message: any, index: number) => {
          const isNewMessage = index >= initialMessageCountRef.current;
          // Only the last agent message should animate, all others skip animation
          const shouldSkipAnimation = index !== lastAgentMessageIndex || lastAgentMessageIndex === -1;

          return (
            <motion.div
              key={message.timestamp || index}
              initial={isNewMessage ? {
                opacity: 0,
                scale: 0.5,
                transformOrigin:
                  message.type === "agent" ? "top left" : "top right",
              } : { opacity: 1, scale: 1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={isNewMessage ? { duration: 0.2 } : { duration: 0 }}
              className={`flex items-${
                message.type === "agent" ? "start" : "end"
              } gap-3 mb-4 w-[100%]`}
            >
              {message.type === "agent" && (
                <div className="relative w-[12%] h-8 bg-gradient-to-br from-[rgba(150,72,254,0.4)] to-[rgba(242,77,182,1)] rounded-tl-[10px] rounded-tr-[10px] rounded-bl-[10px] flex items-center justify-center overflow-hidden mt-3.5">
                  <img
                    src={avatarImage}
                    alt={`${selectedLanguage}-strip`}
                    className="w-9 h-9 rounded-full object-cover -mb-2.5"
                  />
                </div>
              )}
              <div
                className={`p-3.5 ${
                  message.type === "agent"
                    ? "mr-0 w-[88%]"
                    : "bg-white ml-auto shadow-[0px_0px_12px_0px_#A238FF0D] rounded-tr-[18px] rounded-l-[18px]"
                }`}
              >
                {/* Use AnimatedMessage for all agent messages in voice mode */}
                {/* Only the last one animates, older ones display instantly */}
                {message.type === "agent" && 
                 index >= initialMessageCountRef.current &&
                 isVoiceSelected ? (
                  <AnimatedMessage
                    key={`animated-${message.timestamp || index}-${lastAgentMessageIndex}`}
                    text={message.text}
                    speed={80}
                    isLoading={isLoading}
                    skipAnimation={shouldSkipAnimation}
                    className="text-sm leading-5 text-gray-800 font-normal"
                  />
                ) : (
                  <p className="text-sm leading-5 text-gray-800 font-normal">
                    {message.text}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </>
    );
  }, [messages, isLoading, isVoiceSelected, lastAgentMessageIndex, avatarImage, selectedLanguage]);

  useEffect(() => {
    let stream: MediaStream | null = null;

    const initializeRecorder = async () => {
      if (isVoiceSelected && selectedLanguage === "gujarati") {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });

          mediaRecorderRef.current = new MediaRecorder(stream, {
            mimeType: "audio/webm",
          });

          mediaRecorderRef.current.ondataavailable = (event) => {
            if (event.data.size > 0) {
              setAudioChunks((prev) => [...prev, event.data]);
            }
          };

          setIsRecording(true);
        } catch (error) {
          console.error("Error initializing MediaRecorder:", error);
          alert("Failed to access microphone. Please check permissions.");
        }
      }
    };

    initializeRecorder();

    return () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      setIsRecording(false);
      setAudioChunks([]);
    };
  }, [isVoiceSelected, selectedLanguage]);

  useMicVAD({
    onSpeechStart: () => {
      console.log("Speech started");
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "recording" &&
        isRecording
      ) {
        vadActiveRef.current = true;
        setAudioChunks([]); // Reset chunks
        mediaRecorderRef.current.start(100); // Collect data every 100ms
        console.log("Started recording");
      }
    },
    onSpeechEnd: async () => {
      console.log("Speech ended");
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        vadActiveRef.current = false;
        mediaRecorderRef.current.stop();
        console.log("Stopped recording");

        // Wait a brief moment for the last chunks to be collected
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (
          audioChunks.length > 0 &&
          socketRef.current?.readyState === WebSocket.OPEN
        ) {
          try {
            const audioBlob = new Blob(audioChunks, { type: "audio/webm" });
            const reader = new FileReader();

            reader.onloadend = () => {
              const base64AudioData = (reader.result as string).split(",")[1];
              console.log("Sending audio data in onSpeechEnd", base64AudioData);
              socketRef.current?.send(
                JSON.stringify({
                  type: "audio",
                  audio: base64AudioData,
                })
              );
            };

            reader.readAsDataURL(audioBlob);
            setAudioChunks([]); // Clear chunks after sending
          } catch (error) {
            console.error("Error processing audio:", error);
          }
        } else {
          console.log("No audio chunks to send or WebSocket not open", {
            chunksLength: audioChunks.length,
            wsState: socketRef.current?.readyState,
          });
        }
      }
    },
    onVADMisfire: () => {
      console.log("VAD misfire");
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      vadActiveRef.current = false;
      setAudioChunks([]);
    },
  });

  // Additional cleanup
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      setAudioChunks([]);
      vadActiveRef.current = false;
      setIsRecording(false);
    };
  }, []);

  // Add microphone permission check
  const checkMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop()); // Stop the stream after checking
      return true;
    } catch (error) {
      console.error("Microphone permission error:", error);
      return false;
    }
  };

  useEffect(() => {
    if (isVoiceSelected && selectedLanguage === "gujarati") {
      // Check microphone permission before setting up WebSocket
      checkMicrophonePermission().then((hasPermission) => {
        if (hasPermission) {
          socketRef.current = new WebSocket(
            "wss://e278-48-217-186-132.ngrok-free.app/ws/v1/whisper-gujarati"
          );

          socketRef.current.onopen = () => {
            console.log("WebSocket connected");
            socketRef.current?.send(
              JSON.stringify({
                language: "gujarati",
                chat_history: state?.conversation,
              })
            );
            setIsLoading(false);
            setIsListening(true);
          };

          socketRef.current.onmessage = async (event) => {
            try {
              const msg = JSON.parse(event.data);
              console.log(msg);

              if (msg.type === "response.audio_transcript.done") {
                setMessages((prev) => [
                  ...prev,
                  {
                    type: "user",
                    text: msg.data,
                    timestamp: Date.now(),
                  },
                ]);
                userTranscriptionRef.current = [
                  ...userTranscriptionRef.current,
                  {
                    data: msg.data,
                    mode: "voice",
                  },
                ];
                setTranscribedParagraph(
                  (prev) => `${prev}\nUser:\n${msg.data}`
                );
              }
            } catch (error) {
              console.error("Error processing message:", error);
            }
          };

          socketRef.current.onerror = (error) => {
            console.error("WebSocket error:", error);
            setIsListening(false);
            setIsLoading(false);
          };
        } else {
          setIsListening(false);
          setIsLoading(false);
          alert("Please enable microphone access to use voice chat.");
        }
      });

      return () => {
        socketRef.current?.close();
        setIsListening(false);
      };
    }
  }, [isVoiceSelected, selectedLanguage]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]); // Scroll whenever messages update

  // Make sure to update streamRef when mediaStream changes
  useEffect(() => {
    if (mediaStream) {
      streamRef.current = mediaStream;
    }
  }, [mediaStream]);

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [messages]); // Scroll whenever messages update

  // Initialize WebSocket connection
  useEffect(() => {
    if (!isVoiceSelected) {
      const startWebSocket = () => {
        socketRef.current = new WebSocket(WEBSOCKET_URL || "");

        socketRef.current.onopen = () => {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current?.send(
              JSON.stringify({
                language: selectedLanguage,
                chat_history: state?.conversation,
                agent_name: agent_name ?? DEFAULT_AGENT_NAME,
              })
            );
            if (!conversationId) {
              socketRef.current?.send(
                JSON.stringify({
                  type: "text",
                  text: "Hi",
                  language: selectedLanguage,
                })
              );
            }
          }
        };

        socketRef.current.onmessage = async (event: any) => {
          try {
            const msg = JSON.parse(event.data);
            setOptions(msg?.options?.options);

            if (msg?.options?.endOfConversation) {
              setIsEnd(true);
            }
            if (
              msg.type === "response.text" ||
              msg.type === "response.audio_transcript.done" ||
              msg.type === "response.text.done"
            ) {
              setIsChatLoading(false);
              setMessages((prev) => [
                ...prev,
                {
                  type: "agent",
                  text: msg.data,
                  timestamp: Date.now(),
                },
              ]);
              agentTranscriptionRef.current = [
                ...agentTranscriptionRef.current,
                {
                  data: msg.data,
                  mode: "text",
                },
              ];
              setTranscribedParagraph(
                (prev) => `${prev}\nAgent:\n${msg.data}\n`
              );
            }

            if (
              msg.type === "conversation.input.text.done" &&
              msg.data?.trim() !== "Hi" &&
              msg.data?.trim()?.length > 0
            ) {
              setMessages((prev) => [
                ...prev,
                {
                  type: "user",
                  text: msg.data,
                  timestamp: Date.now(),
                },
              ]);
              userTranscriptionRef.current = [
                ...userTranscriptionRef.current,
                {
                  data: msg.data,
                  mode: "text",
                },
              ];
              setTranscribedParagraph((prev) => `${prev}\nUser:\n${msg.data}`);
            }
          } catch (error) {
            console.error("Error processing message:", error);
          }
        };
      };

      startWebSocket();
      return () => socketRef.current?.close();
    }
  }, [selectedLanguage, isVoiceSelected]);

  useEffect(() => {
    if (isVoiceSelected && instructions) {
      startListening();
      cleanup();
    } else {
      stopListening();
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close().catch((err) => {
          console.error("Error closing AudioContext:", err);
        });
      }
      if (
        socketRef.current &&
        socketRef.current?.readyState === WebSocket.OPEN
      ) {
        socketRef.current.close();
      }
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      return () => {
        audioContextRef.current?.close();
      };
    }
  }, [isVoiceSelected, instructions]);

  useEffect(() => {
    if (state?.conversation) {
      setTranscribedParagraph(state?.conversation);
      const newMessages = processConversationSummary(state?.conversation || "");
      setMessages(isVoiceSelected ? [] : newMessages);
      // Track initial message count for preloaded messages
      // For voice mode, set to 0 so all new messages are treated as new and animate
      initialMessageCountRef.current = isVoiceSelected ? 0 : newMessages.length;
    }
  }, [isVoiceSelected]);

  useEffect(() => {
    if (isAudioCompleted && isEnd) {
      toggleMute(true);
    }
  }, [isAudioCompleted, isEnd]);

  const configureData = (channel: RTCDataChannel) => {
    const selectedLang = languageOptions.find(
      (lang) => lang.value === selectedLanguage
    );
    const event = {
      type: "session.update",
      session: {
        modalities: ["text", "audio"],
        input_audio_noise_reduction: {
          type: INPUT_AUDIO_NOISE_REDUCTION_TYPE.FAR_FIELD,
        },
        input_audio_transcription: {
          model: SYMPTOM_COLLECTOR_TRANSCRIPTION_MODEL,
          ...(OPENAI_SUPPORTED_LANGUAGES.includes(
            selectedLang?.languageCode || "en"
          ) && {
            language: selectedLang?.languageCode || "en",
          }),
        },
        tools: [],
      },
    };
    channel.send(JSON.stringify(event));
  };

  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current || !mediaStream) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      if (isMuted) {
        setSoundLevel(1);
        return;
      }

      try {
        analyser.getByteFrequencyData(dataArray);

        let maxValue = 0;
        for (let i = 0; i < dataArray.length; i++) {
          if (dataArray[i] > maxValue) {
            maxValue = dataArray[i];
          }
        }

        let newLevel = 1;
        if (maxValue > 0) {
          newLevel = Math.max(1, Math.min(10, 1 + Math.floor(maxValue / 15)));
        }

        setSoundLevel((prev) => {
          const delta = newLevel - prev;
          return prev + delta * 0.5;
        });

        if (!isMuted) {
          animationFrameRef.current = requestAnimationFrame(updateLevel);
        }
      } catch (error) {
        console.error("Error in updateLevel:", error);
      }
    };

    updateLevel();
  }, [isMuted, mediaStream]);

  const setupAudioAnalysis = useCallback(
    (stream: MediaStream) => {
      try {
        // Cleanup existing
        if (
          audioContextRef.current &&
          audioContextRef.current.state !== "closed"
        ) {
          audioContextRef.current.close().catch((err) => {
            console.error("Error closing AudioContext:", err);
          });
        }
        if (sourceRef.current) {
          sourceRef.current.disconnect();
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.1;
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        sourceRef.current = source;

        if (!isMuted) {
          monitorAudioLevel();
        } else {
          setSoundLevel(1);
        }
      } catch (error) {
        console.error("Error in setupAudioAnalysis:", error);
      }
    },
    [isMuted, monitorAudioLevel]
  );

  const checkExtractOptions = async (transcript: string) => {
    const payload = {
      llm_response: transcript,
      language: selectedLanguage,
    };
    const extractOptionsRes = await fetchExtractOptions(payload, jwtToken);
    setIsEnd(extractOptionsRes);
  };

  const startListening = async () => {
    if (isConnectionActive(peerConnectionRef.current)) {
      // Connection already active, reusing existing connection
      return;
    }
    if (!peerConnectionRef.current) {
      setIsLoading(true);
      try {
        // Check permissions first
        const hasPermission = await checkAndRequestPermissions();
        if (!hasPermission) {
          throw new Error("Microphone permission not granted");
        }

        // Create RTCPeerConnection with explicit configuration for Safari
        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:stun1.l.google.com:19302" },
          ],
        });

        // Add connection state logging with more detail
        pc.oniceconnectionstatechange = () => {
          if (pc.iceConnectionState === "failed") {
            stopListening();
          }
        };

        setPeerConnection(pc);

        // Try to get audio stream with more detailed error handling
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              channelCount: 1,
              sampleRate: 48000,
            },
          });
        } catch (mediaError: any) {
          console.error(
            "MediaDevices error:",
            mediaError.name,
            mediaError.message
          );
          throw new Error(`Failed to access microphone: ${mediaError.message}`);
        }

        setMediaStream(stream);

        // Initialize audio context first
        const audioContext = await initializeAudioContext();

        try {
          // Create audio processing pipeline
          const userSource = audioContext.createMediaStreamSource(stream);
          const destination = audioContext.createMediaStreamDestination();

          // Add a gain node to control volume
          const gainNode = audioContext.createGain();
          gainNode.gain.value = 1.0; // Normal volume

          // Connect the audio nodes
          userSource.connect(gainNode).connect(destination);

          // Add tracks to peer connection
          stream.getTracks().forEach((track) => {
            pc.addTransceiver(track, {
              direction: "sendrecv",
              streams: [stream],
            });
          });

          pc.ontrack = (event) => {
            if (event.track.kind === "audio") {
              const agentAudioStream = event.streams[0];
              const agentAudio = document.createElement("audio");
              agentAudio.srcObject = agentAudioStream;
              agentAudio.autoplay = true;
              document.body.appendChild(agentAudio);

              const agentSource =
                audioContext.createMediaStreamSource(agentAudioStream);
              const agentGain = audioContext.createGain();
              agentGain.gain.value = 1.0;

              agentSource.connect(agentGain).connect(destination);
            }
          };

          // Create data channel with explicit configuration
          const dataChannel = pc.createDataChannel("oai-events", {
            ordered: true,
          });
          setDataChannel(dataChannel);

          dataChannel.addEventListener("open", () => {
            configureData(dataChannel);
          });

          dataChannel.addEventListener("message", async (ev) => {
            const data = JSON.parse(ev.data);

            if (data?.type === "session.created") {
              const currentStream = streamRef.current || mediaStream;

              if (currentStream) {
                const audioTracks = currentStream.getAudioTracks();

                // Mute first
                audioTracks.forEach((track) => {
                  track.enabled = false;
                });
                setIsMuted(true);

                // Unmute after 2 seconds
                setTimeout(() => {
                  audioTracks.forEach((track) => {
                    track.enabled = true;
                  });
                  setIsMuted(false);
                }, 2000);
              }
            }

            if (data?.type === "session.updated") {
              // if (!conversationId) {
                try {
                  // Load audio file
                  const response = await fetch(patientGreeting);
                  const arrayBuffer = await response.arrayBuffer();

                  // Create audio context
                  const audioContext = new (window.AudioContext ||
                    (window as any).webkitAudioContext)();
                  const audioBuffer = await audioContext.decodeAudioData(
                    arrayBuffer
                  );

                  // Create offline context with required sample rate
                  const offlineContext = new OfflineAudioContext({
                    numberOfChannels: 1, // mono
                    length: Math.ceil(audioBuffer.duration * 24000), // adjust length for new sample rate
                    sampleRate: 24000, // required 24kHz
                  });

                  // Create buffer source
                  const source = offlineContext.createBufferSource();
                  source.buffer = audioBuffer;
                  source.connect(offlineContext.destination);
                  source?.start();

                  // Render audio
                  const renderedBuffer = await offlineContext.startRendering();

                  // Convert to 16-bit PCM
                  const pcm16 = new Int16Array(renderedBuffer.length);
                  const channelData = renderedBuffer.getChannelData(0);

                  for (let i = 0; i < renderedBuffer.length; i++) {
                    const s = Math.max(-1, Math.min(1, channelData[i]));
                    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
                  }

                  // Convert to base64
                  const base64Audio = btoa(
                    String.fromCharCode.apply(
                      null,
                      new Uint8Array(pcm16.buffer) as any
                    )
                  );

                  const audioData = {
                    type: "input_audio_buffer.append",
                    audio: base64Audio,
                    event_id: generateUUID,
                  };

                  dataChannel.send(JSON.stringify(audioData));
                } catch (error) {
                  console.error("Error sending audio greeting:", error);
                }
              // }
              setTimeout(() => {
                setIsLoading(false);
              }, 2000);
            }

            if (
              data?.type ===
                "conversation.item.input_audio_transcription.completed" &&
              data.transcript?.trim() !== "Hi" &&
              selectedLanguage !== "gujarati"
            ) {
              setTranscribedParagraph(
                (prev) => `${prev}\nUser:\n${data.transcript}`
              );
              userTranscriptionRef.current = [
                ...userTranscriptionRef.current,
                {
                  data: data.transcript,
                  mode: "voice",
                },
              ];
              setMessages((prev) => [
                ...prev,
                {
                  type: "user",
                  text: data.transcript,
                  timestamp: Date.now(),
                },
              ]);
            }

            if (data?.type === "response.audio_transcript.delta") {
              setIsAudioCompleted(false);
              // Update the ref directly
              // currentIncrementalTextRef.current += data.delta;

              // setMessages((prev) => {
              //   const newMessages = [...prev];
              //   if (
              //     newMessages.length > 0 &&
              //     newMessages[newMessages.length - 1].type === "agent"
              //   ) {
              //     // Update existing agent message
              //     newMessages[newMessages.length - 1].text =
              //       currentIncrementalTextRef.current;
              //   } else {
              //     // Create new agent message if none exists or last message was from user
              //     newMessages.push({
              //       type: "agent",
              //       text: currentIncrementalTextRef.current,
              //       timestamp: Date.now(),
              //     });
              //   }
              //   return newMessages;
              // });
            }

            if (data?.type === "response.audio_transcript.done") {
              setTranscribedParagraph(
                (prev) => `${prev}\nAgent:\n${data.transcript}\n`
              );
              agentTranscriptionRef.current = [
                ...agentTranscriptionRef.current,
                {
                  data: data.transcript,
                  mode: "voice",
                },
              ];
              currentIncrementalTextRef.current = "";
              setMessages((prev) => {
                const newMessages = [...prev];
              //   // Check if the last message is from agent and update it, otherwise add new
              //   if (
              //     newMessages.length > 0 &&
              //     newMessages[newMessages.length - 1].type === "agent"
              //   ) {
              //     newMessages[newMessages.length - 1] = {
              //       ...newMessages[newMessages.length - 1],
              //       text: data.transcript,
              //       timestamp: Date.now(),
              //     };
              //   } else {
                  newMessages.push({
                    type: "agent",
                    text: data.transcript,
                    timestamp: Date.now(),
                  });
                // }
                return newMessages;
              });
              if (
                agentTranscriptionRef.current?.length > EXTRACT_OPTIONS_COUNT
              ) {
                checkExtractOptions(data.transcript);
              }
            }

            if (data?.type === "output_audio_buffer.stopped") {
              setIsAudioCompleted(true);
            }
          });

          // Create and configure MediaRecorder with explicit mime type check
          const merged = destination.stream;

          const mimeType = isIOS() ? "audio/mp4" : "audio/webm;codecs=opus";

          let recorder: MediaRecorder;
          if (MediaRecorder.isTypeSupported(mimeType)) {
            recorder = new MediaRecorder(merged, {
              mimeType,
              audioBitsPerSecond: 128000,
            });
          } else {
            // Fallback to default
            recorder = new MediaRecorder(merged);
          }

          recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              setConversationChunks((prev) => [...prev, event.data]);
            }
          };

          recorder.start(1000); // Record in 1-second chunks
          setMergedRecorder(recorder);

          // Create and set offer
          const offer = await pc.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: false,
          });
          await pc.setLocalDescription(offer);

          try {
            const selectedLang = languageOptions.find(
              (lang) => lang.value === selectedLanguage
            );
            const ephemeralKey = await startAzureOpenAISession(
              agent_voice ?? DEFAULT_VOICE,
              instructions,
              selectedLang?.languageCode || "en"
            );
            if (!ephemeralKey) {
              throw new Error(
                "Failed to create session - no ephemeral key received"
              );
            }

            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
              audioRef.current.src = ""; // Clear the source
              audioRef.current.remove(); // Remove the element
              audioRef.current = null;
            }

            const answer = await establishWebRtcConnection(ephemeralKey, offer);

            if (!answer) {
              throw new Error("No answer received from server");
            }

            try {
              await pc.setRemoteDescription({ sdp: answer, type: "answer" });
            } catch (sdpError) {
              console.error("SDP Error:", sdpError);
              throw new Error("Failed to set remote description");
            }

            setupAudioAnalysis(stream);
            setIsListening(true);
          } catch (connectionError) {
            console.error("Connection Error:", connectionError);
            throw connectionError;
          }
        } catch (processingError) {
          console.error("Audio processing error:", processingError);
          if (audioContextRef.current) {
            audioContextRef.current.close().catch(console.error);
          }
          throw new Error("Failed to setup audio processing");
        }
        peerConnectionRef.current = pc;
      } catch (error: unknown) {
        console.error("Detailed error:", error);

        const webRTCError = error as Error & WebRTCError;
        let errorMessage = "Failed to start recording. ";

        // More detailed error handling
        if (
          webRTCError.name === "NotAllowedError" ||
          webRTCError.message?.includes("permission")
        ) {
          errorMessage +=
            "Please allow microphone access in your browser settings and refresh the page.";
        } else if (
          webRTCError.name === "NotFoundError" ||
          webRTCError.message?.includes("microphone")
        ) {
          errorMessage +=
            "Could not access your microphone. Please make sure it's properly connected and not in use by another application.";
        } else if (webRTCError.name === "NotReadableError") {
          errorMessage +=
            "Could not access your microphone. It might be in use by another application.";
        } else if (webRTCError.message?.includes("session")) {
          errorMessage += "Failed to create session. Please try again.";
        } else if (webRTCError.message?.includes("SDP")) {
          errorMessage +=
            "Connection setup failed. Please refresh the page and try again.";
        } else {
          console.error("Unknown error:", webRTCError);
          errorMessage += `${
            webRTCError.message || "An unexpected error occurred"
          }. Please try again.`;
        }

        alert(errorMessage);

        // Cleanup on error
        if (peerConnection) {
          peerConnection.close();
          setPeerConnection(null);
        }
        if (mediaStream) {
          mediaStream.getTracks().forEach((track) => track.stop());
          setMediaStream(null);
        }
        setIsListening(true);
        setIsLoading(false);
      }
    }
  };

  const stopListening = async () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close().catch((err) => {
        console.error("Error closing AudioContext:", err);
      });
      audioContextRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    if (dataChannel) dataChannel.close();
    if (peerConnection) peerConnection.close();
    if (mediaStream) mediaStream.getTracks().forEach((track) => track.stop());

    if (mergedRecorder && mergedRecorder.state !== "inactive") {
      mergedRecorder.stop();
    }

    try {
      peerConnectionRef.current = null;
      setIsListening(false);
      setPeerConnection(null);
      setMediaStream(null);
      setMergedRecorder(null);
    } catch (error) {
      console.error("Error in conversation handling:", error);
      alert("Failed to process conversation. Please try again.");
    }
    setDataChannel(null);
    setPeerConnection(null);
    setMediaStream(null);
    setMergedRecorder(null);
    setSoundLevel(1);
    setIsListening(false);
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
  };

  // Handle text submission with logging
  const handleSubmit = (value: any) => {
    if (value?.trim() && socketRef.current?.readyState === WebSocket.OPEN) {
      setIsChatLoading(true);
      const message = {
        type: "text",
        text: value,
        language: selectedLanguage,
      };

      try {
        socketRef.current.send(JSON.stringify(message));

        setMessages((prev: any) => [
          ...prev,
          {
            type: "user",
            text: value,
            timestamp: Date.now(),
          },
        ]);
        userTranscriptionRef.current = [
          ...userTranscriptionRef.current,
          { data: value, mode: "text" },
        ];
        setTranscribedParagraph((prev) => `${prev}\nUser:\n${value}`);

        setValue("");
        setOptions([]);
      } catch (error) {
        console.error("Error sending text message:", error);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const cleanup = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        setIsListening(false);
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
    }

    stopRecording();
    lastTranscriptRef.current = "";
  };

  // Update the Yes, Submit to Doctor button click handler
  const handleSubmitToDoctor = async () => {
    setIsSubmitLoading(true);
    cleanup();
    stopListening();
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }

      const uuid = generateUUID();
      // Use the stored audio blobs
      const saveResponse = await saveConversationData(
        uuid,
        userTranscriptionRef.current,
        agentTranscriptionRef.current,
        conversationId,
        conversationChunks,
        transcribedParagraph,
        selectedLanguage,
        um_id,
        patient_unique_id,
        hm_id,
        pam_id,
        jwtToken
      );

      if (saveResponse && saveResponse._id) {
        const summaryResponse = await getSummary(saveResponse._id, jwtToken);
        if (summaryResponse) {
          // Using replace and state together
          navigate(`/symptoms-collector-summary?jwtToken=${jwtToken}`, {
            state: {
              summaryResponse: {
                ...summaryResponse,
                conversation: transcribedParagraph,
              },
              id: saveResponse._id,
              selectedLang: selectedLanguage,
              type: isVoiceSelected ? "voice" : "chat",
              from: "conversation",
              showSubmitBtn: true,
              isAppointmentInQueue: true,
              avatarImage: avatarImage,
            },
            replace: true, // This replaces the current entry in history
          });

          // Then immediately navigate again to the same URL to force a refresh
          navigate(0); // Passing 0 to navigate forces a page refresh
        }
        trackEvent("SC_Patient_SubmittedSymptoms", {
          mode: type,
          submission_id: saveResponse._id,
          summary_length: messages.length,
          language: selectedLanguage,
          appointment_id: pam_id,
          hospital_id: hm_id,
          doctor_name: doctor_name,
          doctor_id: um_id,
          patient_id: patient_unique_id,
          patient_name: patient_name,
          patient_mobile_number: patient_contact_no,
          doctor_speciality: dp_name,
          dp_id: dp_id,
          timestamp: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        });
      }

      audioBlobsRef.current = []; // Clear the stored blobs
      setConversationChunks([]);
      userTranscriptionRef.current = [];
      agentTranscriptionRef.current = [];
    } catch (error) {
      console.error("Error submitting conversation:", error);
      setShowWarningPopup(true);
    }
    setIsSubmitLoading(false);
  };

  // Define toggleMute function with forceState parameter
  const toggleMute = useCallback(
    (forceState?: boolean) => {
      const currentStream = streamRef.current || mediaStream;

      if (currentStream) {
        const audioTracks = currentStream.getAudioTracks();

        // If forceState is provided, use it; otherwise toggle current state
        const newMutedState = forceState !== undefined ? forceState : !isMuted;

        audioTracks.forEach((track) => {
          // enabled should be the opposite of muted
          track.enabled = !newMutedState;
        });

        setIsMuted(newMutedState);
      }
    },
    [mediaStream, isMuted]
  );

  const handleExitPopup = () => {
    setShowExitPopup((prev) => !prev);
  };

  return (
    <Layout>
      <div
        className="flex flex-col relative overflow-hidden"
        style={{
          height: "100dvh",
        }}
      >
        {/* Background Image */}
        <div
          className={`fixed inset-0 ${
            isVoiceSelected ? "top-[58px]" : "top-[70px]"
          }`}
        >
          <img
            src={DDxInputSelectorBg}
            alt="background"
            className="h-full w-full object-cover opacity-5"
          />
        </div>
        {/* Header */}
        <div>
          <div
            className="flex items-center justify-between gap-x-3 p-[17px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(250, 245, 253, 1))",
            }}
          >
            <div className="flex items-center gap-x-2">
              {isVoiceSelected ? (
                <div className="relative w-[32px] h-8 bg-gradient-to-br from-[rgba(150,72,254,0.4)] to-[rgba(242,77,182,1)] rounded-[10px] flex items-center justify-center overflow-hidden">
                  <img
                    className="absolute top-[57%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-[60px] z-10 animate-slideUpFromBubble"
                    src={avatarImage}
                    alt="client"
                  />
                </div>
              ) : (
                <img
                  src={close}
                  alt="close"
                  className="w-[24px] h-[24px] rounded-full object-cover cursor-pointer"
                  onClick={handleExitPopup}
                />
              )}
              <span className="font-semibold text-base">Add Symptoms</span>
            </div>
            {userTranscriptionRef.current?.length > 0 && (
              <div
                className="flex gap-2 items-center p-2 border border-[#ED8A00] text-[#ED8A00] rounded-full cursor-pointer bg-[#ed8a000f]"
                style={{ fontSize: "12px" }}
                onClick={() => setShowWarningPopup(true)}
              >
                <img
                  src={end}
                  alt="close-visit"
                  style={{ height: 16, width: 16 }}
                  className="rounded-full object-cover"
                />
                {"Restart/End"}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative px-4">
          <div
            className={`relative z-10 gap-3 pt-4 overflow-y-auto no-scrollbar h-full ${
              isEnd ? "pb-[240px]" : isVoiceSelected ? "" : "pb-[130px]"
            }`}
          >

          {/* Content */}
          <div className="relative z-10 gap-3 pb-[200px]">
            {/* Loading Animation */}
            {isLoading && isVoiceSelected ? (
              <div className="flex flex-col items-center justify-center h-full pt-[100px]">
                <img
                  src={speakLoading}
                  alt="loading"
                  className={`transition-all duration-2000 ease-in-out`}
                  width={200}
                  height={120}
                />
                <div className="relative overflow-hidden">
                  <span className="text-lg text-[#454551] font-medium text-center relative">
                    Connecting with the Agent!
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
                  </span>
                </div>
              </div>
            ) : isListening && isVoiceSelected && !isLoading ? (
              <div className="flex flex-col overflow-x-hidden max-h-[380px]">
                {renderVoiceMessages}
              </div>
            ) : (
              <AnimatePresence>
                <div
                className="flex-1 overflow-y-auto"
              >
                {renderMessages}
                {showSkeletonLoaderData && (
                  <div className="flex items-start mb-4 w-full gap-3">
                    <div className="relative w-[12%] h-8 bg-gradient-to-br from-[rgba(150,72,254,0.4)] to-[rgba(242,77,182,1)] rounded-tl-[10px] rounded-tr-[10px] rounded-bl-[10px] flex items-center justify-center overflow-hidden mt-3.5">
                      <img
                        src={avatarImage}
                        alt={`${selectedLanguage}-strip`}
                        className="w-9 h-9 rounded-full object-cover -mb-2.5"
                      />
                    </div>
                    <div className="flex gap-3 items-center mr-0 w-[88%]">
                      <img
                        src={chatTypingIdicator}
                        className="w-12 h-12 mt-2"
                        alt="chat-typing-idicator"
                      />
                    </div>
                  </div>
                )}
                </div>
              </AnimatePresence>
            )}
            <div ref={messagesEndRef} />
            {/* Scroll target for latest message */}
          </div>
        </div>

        {/* Footer */}
      {isEnd && !isVoiceSelected ? (
        <div
          className="fixed-bottom-controls"
          style={{ justifyContent: "center" }}
        >
          <section className="flex justify-center flex-col gap-5 w-70 text-base font-medium tracking-normal leading-loose text-center">
            <button
              className={`flex justify-center overflow-hidden gap-2 self-stretch px-14 py-2 bg-[#ed8a000a] whitespace-nowrap rounded-xl border border-[#ED8A0033] border-solid text-[#ED8A00]`}
              onClick={() => {
                setIsEnd(false);
                toggleMute(false);
              }}
            >
              {LangSpecificMsgData.addMore}
            </button>

            <Button
              className={`flex justify-center overflow-hidden gap-2 self-stretch px-14 py-2 text-white bg-indigo-600 whitespace-nowrap rounded-xl border border-indigo-600 border-solid w-full h-[50px] text-base font-medium`}
              onClick={handleSubmitToDoctor}
              loading={isSubmitLoading}
            >
              {LangSpecificMsgData.yesSubmitDoctor}
            </Button>
            <div className="flex gap-2 justify-center opacity-40 mix-blend-luminosity">
              <img src={secureIcon} alt="secure-icon" />
              <div className="text-xs text-[#454551]">
                Your Data is Secured & only doctor can access it
              </div>
            </div>
          </section>
        </div>
      ) : isVoiceSelected ? (
        <>
          <div className="fixed-bottom-controls flex flex-col justify-between gap-2 w-full">
            <div className="flex items-center w-full max-w-[450px] mx-auto gap-4">
              {!isLoading && (
                <button
                  onClick={() => {
                    handleExitPopup();
                  }}
                  className="control-button"
                  style={{ border: "none", flexShrink: 0 }}
                >
                  <img src={chat} alt="chat" className="w-[70px] h-[70px]" />
                </button>
              )}
              <div className="flex-1 flex flex-col items-center gap-2 min-w-0">
                {/* Audio level indicator */}
                {isListening && !isLoading && !isMuted && (
                  <div
                    className="flex justify-center items-center w-full"
                    style={{
                      maxWidth: "calc(100% - 160px)",
                      minWidth: 0,
                    }}
                  >
                    <VoiceWaveVisualizer
                      isRecording={isListening}
                      isPaused={isMuted}
                      width="100%"
                      height={60}
                    />
                  </div>
                )}
              </div>
              {!isLoading && (
                <button
                  onClick={() => toggleMute()}
                  className="control-button"
                  style={{ border: "none", flexShrink: 0 }}
                >
                  <img
                    src={isMuted ? muteMic : activeMic}
                    alt={isMuted ? "Unmute microphone" : "Mute microphone"}
                    className="w-[70px] h-[70px]"
                  />
                </button>
              )}
            </div>

            <div>
              <span className="text-base text-white">
                {isLoading
                  ? ""
                  : isListening && !isMuted
                  ? messages?.[messages?.length - 1]?.type === "agent"
                    ? LangSpecificMsgData.agentSpeaking + "..."
                    : LangSpecificMsgData.listening + "..."
                  : "Muted"}
              </span>
            </div>
          </div>
        </>
      ) : (
        <div
          className={`bottom-0 left-0 w-full max-w-[450px] mx-auto z-20 fixed ${
            isSafari || isIOSDevice ? "fixed" : ""
          }`}
        >
          {options?.length > 0 && (
            <div className="flex overflow-auto gap-2 items-center m-4 mb-3 no-scrollbar">
              {options?.map((item: any, index: number) => {
                  return (
                    <div
                      key={index}
                      className="flex-grow-0 flex-shrink-0 cursor-pointer relative backdrop-blur-xl"
                      style={{
                        background: "rgba(255, 255, 255, 0.5)",
                        borderRadius: "20px",
                        padding: "8px 16px",
                        zIndex: 1,
                        minWidth: "fit-content",
                        maxWidth: "100%",
                        cursor: "pointer",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0)",
                      }}
                      onClick={() => {
                        handleSubmit(item);
                      }}
                    >
                      <div
                        style={{
                          content: '""',
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0,
                          borderRadius: "20px",
                          padding: "1px",
                          background:
                            "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                          WebkitMask:
                            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                          WebkitMaskComposite: "xor",
                          maskComposite: "exclude",
                          zIndex: -1,
                          pointerEvents: "none",
                        }}
                      />
                      <span
                        style={{
                          background:
                            "linear-gradient(90deg, #564DD5 0%, #7756D6 50%, #9E60D8 100%)",
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          color: "transparent",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          pointerEvents: "none",
                        }}
                      >
                        {item}
                      </span>
                    </div>
                  );
              })}
            </div>
          )}
          <div
            className="p-4 flex flex-col gap-8 bg-white border-t-[0.5px] border-t-[#E2E2EA] [border-radius:25px_25px_0_0]"
            style={{
              boxShadow: "0 -8px 10px rgba(255, 0, 191, 0.08)",
            }}
          >
            <div className="p-[1px] rounded-2xl bg-gradient-to-r from-[#A461D8] via-[#7656D6] to-[#554DD5]">
              <div className="rounded-2xl flex flex-col overflow-hidden bg-white">
                <div className="relative rounded-2xl flex flex-col gap-1 py-1 px-4">
                  <textarea
                    id="symptoms-input"
                    name="symptoms"
                    className="w-full border-none outline-none bg-transparent resize-none text-[#454551] text-sm font-normal placeholder-[#98A2B3] caret-[#4B4AD5] py-2"
                    rows={1}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (value?.trim()) {
                          handleSubmit(value);
                        }
                      }
                    }}
                    placeholder="Type your message here"
                  />
                  <div className="absolute bottom-1.5 right-1.5 flex flex-row items-center justify-between">
                    <div />
                    {value?.length > 0 ? (
                      <div className="flex flex-row items-center gap-4">
                        <button onClick={() => handleSubmit(value)}>
                          <img src={bubble} alt="bubble" />
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isVoiceSelected && !isLoading && (
        <div className="fixed left-0 right-0 bottom-0 w-full max-w-[420px] mx-auto">
          <VoiceBubble />
        </div>
      )}
      </div>

      <WarningPopup
        show={showWarningPopup}
        closePopup={() => setShowWarningPopup(false)}
        handleSubmitToDoctor={handleSubmitToDoctor}
        isSubmitLoading={isSubmitLoading}
        selectedLang={selectedLanguage}
      />

      {showExitPopup && (
        <ConfirmationPopup
          show={showExitPopup}
          closePopup={handleExitPopup}
          handleConfirmation={() => {
            handleExitPopup();
            navigate(-1);
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }}
        />
      )}

      {isEnd && isVoiceSelected && isAudioCompleted && (
        <ConfirmSubmitPopup
          closePopup={() => {
            setIsEnd(false);
            toggleMute(false);
          }}
          handleSubmitToDoctor={handleSubmitToDoctor}
          isSubmitLoading={isSubmitLoading}
        />
      )}
      </div>
    </Layout>
  );
};

export default Conversation;
